import { useFetch } from "../useFetch";
import { useMemo } from "react";
import type { UserResponse, CreateUserPayload } from "../../core/types/control/user";
import {
	extractBannedUserRecord,
	extractBannedUsersCollection,
	type BannedUserRecord,
} from "../../core/utils/user-account-status";
import { useState } from "react";

// ============================================
// Tipos Auxiliares para el Hook
// ============================================

// Para actualizar, usamos el payload de creación pero todo opcional
// y quitamos el password, ya que suele ir por endpoint separado
type UpdateUserPayload = Partial<Omit<CreateUserPayload, "password">>;

type PaginatedResponse<T> = {
	items: T[];
	meta: {
		pagination: {
			totalItems: number;
			page: number;
			pageSize: number;
			totalPages: number;
		};
	};
};

// ============================================
// GET ALL USERS (PAGINATED)
// ============================================
export function useUsers(page = 1, pageSize = 10) {
	const query = useMemo(() => ({ page, pageSize }), [page, pageSize]);
	// Ahora devuelve una lista de UserResponse

	return useFetch<PaginatedResponse<UserResponse>>({
		endpoint: "/users",
		method: "GET",
		query,
	});
}

// ============================================
// GET USER BY ID
// ============================================
export function useUser(id: number) {
	return useFetch<UserResponse>({
		endpoint: `/users/${id}`,
		method: "GET",
		auto: id > 0,
	});
}

// ============================================
// GET CURRENT USER (SELF)
// ============================================
export function useCurrentUser(userId: number) {
	return useFetch<Record<string, unknown>>({
		endpoint: `/users/${userId}`,
		method: "GET",
		auto: userId > 0,
	});
}

// ============================================
// CREATE USER
// ============================================
export function useCreateUser() {
	// <RespuestaBackend, DatosEnviados>
	const fetch = useFetch<UserResponse, CreateUserPayload>({
		endpoint: "/users",
		method: "POST",
		auto: false,
	});

	const create = (
		data: CreateUserPayload,
		files?: { image_profile?: File; identity_documents?: File[] },
	) => {
		// Si tu useFetch soporta FormData automático para 'files', esto funcionará perfecto
		return fetch.execute({ body: data, files });
	};

	return { ...fetch, create };
}

// ============================================
// UPDATE USER
// ============================================
export function useUpdateUser(id: number) {
	// <RespuestaBackend, DatosEnviados>
	const fetch = useFetch<UserResponse, UpdateUserPayload>({
		endpoint: `/users/${id}`,
		method: "PATCH",
		auto: false,
	});

	const update = (
		data: UpdateUserPayload,
		files?: { image_profile?: File; identity_documents?: File[] },
	) => {
		return fetch.execute({ body: data, files });
	};

	return { ...fetch, update };
}

// ============================================
// DELETE USER
// ============================================
export function useDeleteUser() {
	// Generalmente delete devuelve un mensaje de éxito o void
	const fetch = useFetch<{ message: string }>({
		endpoint: "/users",
		method: "DELETE",
		auto: false,
	});

	const remove = (id: number) => {
		// Sobreescribimos el endpoint base para incluir el ID
		return fetch.execute({ endpoint: `/users/${id}` });
	};

	return { ...fetch, remove };
}

// ============================================
// VERIFY USER PHONE
// ============================================
export function useVerifyUserPhone(id: number) {
	// Asumimos que al verificar devuelve el UserResponse actualizado
	const fetch = useFetch<UserResponse, { phone: string }>({
		endpoint: `/users/${id}/verify-phone`,
		method: "POST",
		auto: false,
	});

	const verifyPhone = (phone: string) => {
		return fetch.execute({ body: { phone } });
	};

	return { ...fetch, verifyPhone };
}

// ============================================
// CHANGE USER PASSWORD
// ============================================
export function useChangeUserPassword(userId: number) {
	const fetch = useFetch<
		{ message: string },
		{ currentPassword: string; newPassword: string }
	>({
		endpoint: `/users/${userId}/change-password`,
		method: "PATCH",
		auto: false,
	});

	const changePassword = (currentPassword: string, newPassword: string) => {
		return fetch.execute({ body: { currentPassword, newPassword } });
	};

	return { ...fetch, changePassword };
}

// ============================================
// SET USER STATUS (ACTIVATE / DEACTIVATE)
// ============================================
export function useSetUserStatus(id: number, bannedUserId?: number) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const { execute: fetchBannedUsers } = useFetch<unknown>({
		endpoint: "/banned-user",
		method: "GET",
		auto: false,
	});
	const { execute: patchBannedUser } = useFetch<
		{ banned_user?: BannedUserRecord },
		Record<string, unknown>
	>({
		endpoint: "/banned-user/0",
		method: "PATCH",
		auto: false,
	});
	const { execute: createBannedUser } = useFetch<unknown, Record<string, unknown>>({
		endpoint: "/banned-user",
		method: "POST",
		auto: false,
	});

	type SuspensionConfig = {
		mode?: "indefinite" | "until";
		untilDate?: string;
		reason?: string;
	};

	const setStatus = async (active: boolean, config?: SuspensionConfig) => {
		if (!id) {
			throw new Error("ID de usuario no valido.");
		}

		setLoading(true);
		setError(null);

		try {
			let currentRecord: BannedUserRecord | undefined;

			if (bannedUserId) {
				currentRecord = { id: bannedUserId, user_id: id };
			} else {
				const collection = await fetchBannedUsers({
					endpoint: "/banned-user",
				});
				const records = extractBannedUsersCollection(collection);
				currentRecord = records.find((record) => record.user_id === id);
			}

			if (currentRecord?.id) {
				const statusResult = await patchBannedUser({
					endpoint: `/banned-user/${currentRecord.id}`,
					body: { is_active: active },
				});

				if (!active) {
					const suspensionDate =
						config?.mode === "until" && config.untilDate
							? config.untilDate
							: null;

					await patchBannedUser({
						endpoint: `/banned-user/${currentRecord.id}`,
						body: {
							is_banned: true,
							reason_ban: config?.reason,
							date_unban: suspensionDate,
						},
					});
				}

				return statusResult;
			}

			const suspensionDate =
				!active && config?.mode === "until" && config.untilDate
					? config.untilDate
					: undefined;

			const created = await createBannedUser({
				body: {
					user_id: id,
					...(active
						? {}
						: {
								is_active: false,
								is_banned: true,
								reason_ban: config?.reason,
								date_unban: suspensionDate,
							}),
				},
			});

			const record = extractBannedUserRecord(created);
			if (!record) {
				throw new Error("No se pudo actualizar el estado del usuario.");
			}

			return record;
		} catch (caught) {
			const normalizedError =
				caught instanceof Error
					? caught
					: new Error("No se pudo actualizar el estado del usuario.");
			setError(normalizedError);
			throw normalizedError;
		} finally {
			setLoading(false);
		}
	};

	return { setStatus, loading, error };
}
